import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Alert, Box, Button } from "@mui/material";
import { translateKey, type Translate } from "../../i18n/translate";
import { knownErrorMessage } from "../../utils/apiError";
import type enCommon from "../../i18n/locales/en/common.json";

/** A key under `common:actions`: an infinitive phrase, "save the room". */
export type ActionKey = keyof (typeof enCommon)["actions"];

/**
 * The one toast the CMS uses.
 *
 * Rules (UI-FIX): every save or mutation shows a toast, success is light
 * green, failure is light red, and they all sit in the bottom-RIGHT corner.
 * The colours come from the theme's standard `Alert` overrides
 * (`standardSuccess` / `standardError`); do NOT switch to `variant="filled"`
 * or to a bare `<Snackbar message>`, which renders dark grey and breaks the
 * convention.
 *
 * Write the text so it says WHAT happened to WHAT ("Room 204 updated"), not
 * "Saved". Errors: use {@link describeError} so a raw `err.message` never
 * reaches the screen unframed.
 *
 * Do not mount a second `<Snackbar>` in a page. If a call site needs a new
 * behaviour, extend this component.
 */

export type ToastSeverity = "success" | "error";

export interface ToastAction {
  label: string;
  /** Navigates (same tab) when clicked. */
  href?: string;
  onClick?: () => void;
}

export interface ToastOptions {
  /** A link/button shown inside the toast (e.g. "View rollout"). */
  action?: ToastAction;
  /** Override the auto-hide delay, in ms. `0` keeps it until dismissed. */
  durationMs?: number;
}

interface ToastItem {
  id: number;
  severity: ToastSeverity;
  message: string;
  action?: ToastAction;
  durationMs: number;
}

export interface ToastApi {
  success: (message: string, options?: ToastOptions) => void;
  error: (message: string, options?: ToastOptions) => void;
  dismiss: (id?: number) => void;
}

/** Success toasts fade quickly; errors stay long enough to be read. */
export const SUCCESS_DURATION_MS = 4000;
export const ERROR_DURATION_MS = 8000;
/** Older toasts are dropped beyond this, so a bulk action cannot bury the UI. */
export const MAX_VISIBLE_TOASTS = 4;

const noop: ToastApi = {
  success: () => {},
  error: () => {},
  dismiss: () => {},
};

const Ctx = createContext<ToastApi>(noop);

/**
 * `useToast()` outside a `ToastProvider` is a silent no-op on purpose: pages
 * and dialogs that toast can still be rendered alone in a unit test. The app
 * mounts the provider once, in `main.tsx`.
 */
export function useToast(): ToastApi {
  return useContext(Ctx);
}

/**
 * "Could not save the room: <reason>." `actionKey` is under `common:actions`.
 * A reason that does not read like a sentence (a code, JSON) is dropped.
 */
export function describeError(
  t: Translate,
  actionKey: ActionKey,
  err: unknown,
  params?: Record<string, string | number>
): string {
  const action = translateKey(t, `common:actions.${actionKey}`, params);
  const raw =
    knownErrorMessage(t, err) ??
    (err instanceof Error ? err.message : typeof err === "string" ? err : "");
  const reason = raw.trim();
  const readable =
    reason.length > 0 &&
    reason.length <= 200 &&
    /\s/.test(reason) &&
    !/[{}[\]]/.test(reason) &&
    !/^[A-Z_]+$/.test(reason);
  if (!readable) return translateKey(t, "common:couldNot", { action });
  return translateKey(t, "common:couldNotBecause", {
    action,
    reason: reason.replace(/[.\s]+$/, ""),
  });
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id?: number) => {
    setItems((cur) => (id === undefined ? [] : cur.filter((t) => t.id !== id)));
  }, []);

  const push = useCallback(
    (severity: ToastSeverity, message: string, options?: ToastOptions) => {
      const id = nextId.current++;
      const durationMs =
        options?.durationMs ??
        (severity === "error" ? ERROR_DURATION_MS : SUCCESS_DURATION_MS);
      setItems((cur) =>
        [
          ...cur,
          { id, severity, message, action: options?.action, durationMs },
        ].slice(-MAX_VISIBLE_TOASTS)
      );
    },
    []
  );

  const api = useMemo<ToastApi>(
    () => ({
      success: (m, o) => push("success", m, o),
      error: (m, o) => push("error", m, o),
      dismiss,
    }),
    [push, dismiss]
  );

  return (
    <Ctx.Provider value={api}>
      {children}
      <ToastStack items={items} onDismiss={dismiss} />
    </Ctx.Provider>
  );
}

function ToastStack({
  items,
  onDismiss,
}: {
  items: ToastItem[];
  onDismiss: (id: number) => void;
}) {
  if (items.length === 0) return null;
  return (
    <Box
      data-testid="toast-stack"
      sx={{
        position: "fixed",
        right: 16,
        bottom: 16,
        zIndex: (theme) => theme.zIndex.snackbar,
        display: "flex",
        flexDirection: "column",
        gap: 1,
        maxWidth: "min(480px, calc(100vw - 32px))",
        // The stack itself must not swallow clicks aimed at the page behind.
        pointerEvents: "none",
      }}
    >
      {items.map((t) => (
        <ToastCard key={t.id} item={t} onDismiss={onDismiss} />
      ))}
    </Box>
  );
}

function ToastCard({
  item,
  onDismiss,
}: {
  item: ToastItem;
  onDismiss: (id: number) => void;
}) {
  const { id, durationMs } = item;
  useEffect(() => {
    if (durationMs <= 0) return;
    const timer = setTimeout(() => onDismiss(id), durationMs);
    return () => clearTimeout(timer);
  }, [id, durationMs, onDismiss]);

  const { action } = item;
  return (
    <Alert
      severity={item.severity}
      variant="standard"
      onClose={() => onDismiss(id)}
      data-testid={`toast-${item.severity}`}
      sx={{ pointerEvents: "auto", boxShadow: 3 }}
    >
      {item.message}
      {action && (
        <Button
          size="small"
          color="inherit"
          href={action.href}
          onClick={() => {
            action.onClick?.();
            onDismiss(id);
          }}
          sx={{ ml: 1, textDecoration: "underline" }}
        >
          {action.label}
        </Button>
      )}
    </Alert>
  );
}

export default ToastProvider;
